import {
  WSButton,
  WSButtonProps,
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSList,
  WSLoader,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  Currency,
  ListPayoutItemsCollectionParameter,
  Payout
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { queriesPayouts } from "@query";
import { useQueryInvoicesByIds } from "../../../../query/invoices/queries/useQueryInvoicesByIds";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { makeNegative } from "../../utils/makeNegative";
import { getPayoutInvoiceIds, getTotals } from "../../utils/utils";

export interface PayoutSummaryPanelProps extends WSMarginProps {
  payout: Payout;
}

export const PayoutSummaryPanel: React.FC<PayoutSummaryPanelProps> = props => {
  const payout = props.payout;
  const history = useHistory();
  const currency = (payout.currency ?? Currency.USD) as Currency;

  const queryPayoutItems = queriesPayouts.listPayoutItems.useQueryAll(
    {
      payoutId: payout.id,
      collection: ListPayoutItemsCollectionParameter.PAYOUT
    },
    { enabled: payout.itemCounts.payout > 0 }
  );
  const queryClearingItems = queriesPayouts.listPayoutItems.useQueryAll(
    {
      payoutId: payout.id,
      collection: ListPayoutItemsCollectionParameter.CLEARING
    },
    { enabled: payout.itemCounts.clearing > 0 }
  );
  const queryFutureDebitItems = queriesPayouts.listPayoutItems.useQueryAll(
    {
      payoutId: payout.id,
      collection: ListPayoutItemsCollectionParameter.FUTURE_DEBIT
    },
    { enabled: payout.itemCounts.futureDebit > 0 }
  );

  const queryPreviousPayout = queriesPayouts.getPayout.useQuery(
    { payoutId: payout.previousPayoutId as string },
    { enabled: !!payout.previousPayoutId }
  );
  const previousPayout = queryPreviousPayout.data;

  const queryPreviousFutureDebitItems =
    queriesPayouts.listPayoutItems.useQueryAll(
      {
        payoutId: previousPayout?.id as string,
        collection: ListPayoutItemsCollectionParameter.FUTURE_DEBIT
      },
      {
        enabled:
          !!previousPayout?.id && previousPayout.itemCounts.futureDebit > 0
      }
    );

  const payoutItems = useMemo(
    () => queryPayoutItems.flatData ?? [],
    [queryPayoutItems.flatData]
  );
  const clearingItems = useMemo(
    () => queryClearingItems.flatData ?? [],
    [queryClearingItems.flatData]
  );
  const futureDebitItems = useMemo(
    () => queryFutureDebitItems.flatData ?? [],
    [queryFutureDebitItems.flatData]
  );
  const previousFutureDebitItems = useMemo(
    () => queryPreviousFutureDebitItems.flatData ?? [],
    [queryPreviousFutureDebitItems.flatData]
  );

  const invoiceIds = useMemo(
    () =>
      getPayoutInvoiceIds([
        ...payoutItems,
        ...clearingItems,
        ...futureDebitItems
      ]),
    [payoutItems, clearingItems, futureDebitItems]
  );

  const queryInvoicesByIds = useQueryInvoicesByIds(invoiceIds);

  const totals = useMemo(
    () =>
      getTotals({
        payout,
        payoutItems,
        clearingItems,
        futureDebitItems,
        previousFutureDebitItems
      }),
    [
      payout,
      payoutItems,
      clearingItems,
      futureDebitItems,
      previousFutureDebitItems
    ]
  );

  const isLoadingItems =
    (payout.itemCounts.payout > 0 && queryPayoutItems.isInitialLoading) ||
    (payout.itemCounts.clearing > 0 && queryClearingItems.isInitialLoading) ||
    (payout.itemCounts.futureDebit > 0 &&
      queryFutureDebitItems.isInitialLoading) ||
    (!!payout.previousPayoutId && queryPreviousPayout.isInitialLoading) ||
    (!!previousPayout?.id &&
      previousPayout.itemCounts.futureDebit > 0 &&
      queryPreviousFutureDebitItems.isInitialLoading);

  if (isLoadingItems) {
    return <WSLoader.Spinner />;
  }

  return (
    <WSCard
      divider
      header={{
        label: "Payout summary",
        value: {
          pill: queryInvoicesByIds.isLoading
            ? {
                text: "Loading",
                icon: "loader"
              }
            : undefined
        }
      }}
    >
      <WSList gap="2XL">
        {previousPayout && totals.previousFutureDebitItemsTotal !== 0 && (
          <WSElement>
            <Row
              label="Previously Held Items"
              value={formatMoney(
                Math.abs(totals.previousFutureDebitItemsTotal),
                currency
              )}
              button={{
                label: "View",
                onClick: () => {
                  openInNewTab("/member/invoices/payouts/" + previousPayout.id);
                }
              }}
            />
          </WSElement>
        )}

        {totals.payoutItems.length > 0 && (
          <WSList>
            <Row accented label="Payout items" />

            <WSList gap="XS">
              {totals.payoutItems.map(item => {
                const invoice = queryInvoicesByIds.data?.find(
                  invoice => invoice.invoiceId === item.sourceId
                );

                return (
                  <Row
                    key={item.sourceId}
                    label={item.description || ""}
                    value={formatMoney(item.amount, currency)}
                    button={
                      invoice
                        ? {
                            label: "View #" + invoice.invoiceNumber,
                            onClick: () => {
                              openInNewTab(
                                "/member/invoices/" + invoice.invoiceId
                              );
                            }
                          }
                        : undefined
                    }
                  />
                );
              })}
            </WSList>
          </WSList>
        )}

        {totals.clearingItems.length > 0 && (
          <WSList>
            <Row accented label="Clearing items" />

            <WSList gap="XS">
              {totals.clearingItems.map(item => {
                const invoice = queryInvoicesByIds.data?.find(
                  invoice => invoice.invoiceId === item.sourceId
                );

                return (
                  <Row
                    key={item.sourceId}
                    label={item.description || ""}
                    value={formatMoney(item.amount, currency)}
                    button={
                      invoice
                        ? {
                            label: "View #" + invoice.invoiceNumber,
                            onClick: () => {
                              history.push(
                                "/member/invoices/" + invoice.invoiceId
                              );
                            }
                          }
                        : undefined
                    }
                  />
                );
              })}
            </WSList>
          </WSList>
        )}

        {totals.futureDebitItems.length > 0 && (
          <WSList>
            <Row accented label="Future debits" />

            <WSList gap="XS">
              {totals.futureDebitItems.map(item => {
                const invoice = queryInvoicesByIds.data?.find(
                  invoice => invoice.invoiceId === item.sourceId
                );

                return (
                  <Row
                    key={item.sourceId}
                    label={item.description || ""}
                    value={formatMoney(makeNegative(item.amount), currency)}
                    button={
                      invoice
                        ? {
                            label: "View #" + invoice.invoiceNumber,
                            onClick: () => {
                              history.push(
                                "/member/invoices/" + invoice.invoiceId
                              );
                            }
                          }
                        : undefined
                    }
                  />
                );
              })}
            </WSList>
          </WSList>
        )}

        {totals.otherItemsTotal !== 0 && (
          <Row
            label="Other items"
            value={formatMoney(totals.otherItemsTotal, currency)}
          />
        )}
      </WSList>

      <WSDivider my="L" />

      <Row
        accented
        label="Net payout total"
        value={formatMoney(totals.netTotal, currency)}
      />
    </WSCard>
  );
};

type RowProps = {
  label: string;
  value?: string;
  accented?: boolean;
  button?: WSButtonProps<"Link">;
};

const Row: React.FC<RowProps> = props => (
  <WSFlexBox gap="2XL" justify="space-between">
    <WSText.ParagraphSm
      weight={props.accented ? "medium" : undefined}
      color="gray500"
    >
      {props.label}

      {props.button && (
        <WSButton.Link rightIcon="open-tab" size="S" {...props.button} />
      )}
    </WSText.ParagraphSm>
    {props.value && (
      <WSText.ParagraphSm
        weight={props.accented ? "medium" : undefined}
        color="gray700"
      >
        {props.value}
      </WSText.ParagraphSm>
    )}
  </WSFlexBox>
);
